import { ckmeans, equalIntervalBreaks, quantile } from "simple-statistics";

import { HexColor } from "@mapmycustomers/shared/types/AnyColor";
import Currency from "@mapmycustomers/shared/types/Currency";

import Bucket from "../types/Bucket";
import ClassificationMethod from "../types/ClassificationMethod";

const createBucketsFromBreakpoints = (
  currencyId: Currency["id"] | undefined,
  breakPoints: number[],
  colors: HexColor[]
): Bucket[] => {
  return breakPoints
    .map((value) => Math.floor(value))
    .reduce((result: Bucket[], item: number, index: number, breakPoints) => {
      const start = result.length > 0 ? breakPoints[index - 1] : 0;
      result.push({
        id: index,
        color: colors[index % colors.length],
        currencyId,
        range: [Math.floor(start), Math.floor(item)],
        visible: true,
      });
      return result;
    }, []);
};

const calculateBucketsForMethod = (
  classificationMethod: ClassificationMethod,
  bucketCount: number,
  currencyId: Currency["id"] | undefined,
  dataOrBreakpoints: number[], // breakpoints for manual method, data otherwise
  colors: HexColor[]
): Bucket[] => {
  if (!colors.length) {
    return [];
  }

  if (classificationMethod === ClassificationMethod.MANUAL) {
    const buckets = createBucketsFromBreakpoints(currencyId, dataOrBreakpoints, colors);
    buckets.push({
      id: dataOrBreakpoints.length,
      color: colors.at(-1)!,
      currencyId,
      range: [Math.floor(dataOrBreakpoints.at(-1)!)],
      visible: true,
    });
    return buckets;
  } else if (
    [ClassificationMethod.EQUAL_INTERVAL, ClassificationMethod.QUANTILE].includes(
      classificationMethod
    )
  ) {
    let breakPoints: number[] = [];
    if (classificationMethod === ClassificationMethod.EQUAL_INTERVAL) {
      breakPoints = equalIntervalBreaks(dataOrBreakpoints, bucketCount);
      breakPoints.shift();
    } else {
      const quantiles = Array.from(
        { length: bucketCount - 1 },
        (_, i) => (i + 1) * (1 / bucketCount)
      );
      breakPoints = quantile(dataOrBreakpoints, quantiles);
    }
    if (breakPoints.length > 0) {
      const result: Bucket[] = createBucketsFromBreakpoints(currencyId, breakPoints, colors);

      if (classificationMethod === ClassificationMethod.QUANTILE && breakPoints.length > 0) {
        result.push({
          id: breakPoints.length,
          color: colors.at(-1)!,
          currencyId,
          range: [Math.floor(breakPoints.at(-1)!)],
          visible: true,
        });
      }

      return result;
    }
    return [];
  } else {
    const buckets =
      dataOrBreakpoints.length && bucketCount ? ckmeans(dataOrBreakpoints, bucketCount) : [];

    return buckets.reduce((result: Bucket[], bucket: number[], index: number) => {
      const start = bucket[0];
      const end = bucket.length > 1 ? bucket[bucket.length - 1] : undefined;
      result.push({
        id: index,
        color: colors[index % colors.length],
        currencyId,
        range: end ? [start, end] : [start],
        visible: true,
      });
      return result;
    }, []);
  }
};

export default calculateBucketsForMethod;
